import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { mediaUrl } from '@/hooks/useMediaUrl';
import { PostComposer } from '@/components/PostComposer';
import { PostCard } from '@/components/PostCard';
import type { GroupWithMembership, PostWithAuthor } from '@shared/types';

interface GroupMember {
  id: string;
  display_name: string;
  username: string;
  avatar_key: string | null;
  role: string;
}

export function GroupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [group, setGroup] = useState<GroupWithMembership | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [posts, setPosts] = useState<PostWithAuthor[]>([]);
  const [loadingGroup, setLoadingGroup] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchGroup = async () => {
    if (!id) return;
    setLoadingGroup(true);
    try {
      const data = await api.get<GroupWithMembership>(`/groups/${id}`);
      setGroup(data);
    } catch {
      setGroup(null);
    }
    setLoadingGroup(false);
  };

  const fetchMembers = async () => {
    if (!id) return;
    try {
      const data = await api.get<GroupMember[]>(`/groups/${id}/members?limit=10`);
      setMembers(data);
    } catch {
      // ignore
    }
  };

  const fetchPosts = async () => {
    if (!id) return;
    setLoadingPosts(true);
    try {
      const data = await api.get<PostWithAuthor[]>(`/groups/${id}/posts?limit=20`);
      setPosts(data);
    } catch {
      // ignore
    }
    setLoadingPosts(false);
  };

  useEffect(() => {
    fetchGroup();
    fetchMembers();
    fetchPosts();
  }, [id]);

  const handleJoinLeave = async () => {
    if (!group || !id) return;
    setActionLoading(true);
    try {
      if (group.my_role) {
        await api.del(`/groups/${id}/leave`);
        setGroup({ ...group, my_role: null, member_count: group.member_count - 1 });
      } else {
        await api.post(`/groups/${id}/join`);
        setGroup({ ...group, my_role: 'member', member_count: group.member_count + 1 });
      }
    } catch {
      // ignore
    }
    setActionLoading(false);
  };

  if (loadingGroup) {
    return (
      <div className="max-w-5xl mx-auto px-3 py-8 text-center text-sand-500">
        <div className="animate-pulse text-3xl mb-2">{'\u301c\ufe0f'}</div>
        <p className="text-sm">Loading group...</p>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="max-w-5xl mx-auto px-3 py-8 text-center text-sand-500">
        <div className="text-3xl mb-2">{'\ud83e\udd37'}</div>
        <p className="text-sm">Group not found</p>
      </div>
    );
  }

  const isMember = !!group.my_role;

  return (
    <div className="max-w-5xl mx-auto px-3 py-4">
      {/* Group header */}
      <div className="card mb-4 overflow-hidden">
        <div className="h-32 bg-gradient-to-br from-vibe-300 to-vibe-500 flex items-center justify-center">
          {group.image_key ? (
            <img src={mediaUrl(group.image_key)} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-5xl opacity-50">&#127968;&#65039;</span>
          )}
        </div>
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-sand-800">{group.name}</h1>
              {group.description && <p className="text-sm text-sand-600 mt-1">{group.description}</p>}
              <div className="flex gap-3 text-xs text-sand-500 mt-2">
                <span className="font-bold text-sand-700">{group.member_count}</span> members
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                  group.privacy === 'open' ? 'bg-green-100 text-green-700' :
                  group.privacy === 'closed' ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {group.privacy.charAt(0).toUpperCase() + group.privacy.slice(1)}
                </span>
              </div>
            </div>
            <button
              onClick={handleJoinLeave}
              disabled={actionLoading}
              className={`text-xs font-bold py-1.5 px-4 rounded-md transition-colors shrink-0 disabled:opacity-40 ${
                isMember
                  ? 'bg-sand-200 text-sand-600 hover:bg-red-100 hover:text-red-600'
                  : 'btn-primary'
              }`}
            >
              {isMember ? 'Leave Group' : 'Join Group'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex gap-4">
        {/* Main content - wall */}
        <div className="flex-1 min-w-0">
          {isMember && (
            <PostComposer
              wallOwnerId={undefined}
              wallOwnerName={group.name}
              onPostCreated={fetchPosts}
            />
          )}

          {loadingPosts ? (
            <p className="text-center text-sand-500 text-xs py-4">Loading posts...</p>
          ) : posts.length === 0 ? (
            <div className="text-center py-8 text-sand-500">
              <div className="text-3xl mb-2">{'\u301c\ufe0f'}</div>
              <p className="text-sm">No posts in this group yet</p>
            </div>
          ) : (
            posts.map((post) => <PostCard key={post.id} post={post} />)
          )}
        </div>

        {/* Members sidebar */}
        <div className="hidden lg:block w-52 shrink-0">
          <div className="card">
            <div className="card-header">&#128101; Members ({group.member_count})</div>
            <div className="p-2 space-y-1">
              {members.map((m) => (
                <Link
                  key={m.id}
                  to={`/profile/${m.username}`}
                  className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-sand-50 transition-colors"
                >
                  <img src={mediaUrl(m.avatar_key)} alt="" className="w-7 h-7 rounded object-cover shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-sand-700 truncate">{m.display_name}</div>
                    {m.role === 'admin' && (
                      <span className="text-[9px] text-vibe-500 font-bold">Admin</span>
                    )}
                  </div>
                </Link>
              ))}
              {members.length < group.member_count && (
                <p className="text-[10px] text-sand-500 text-center py-1">
                  and {group.member_count - members.length} more
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
